"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import type { Locale } from "./templates/ServicePageTemplate";

/* ------------------------------------------------------------------ */
/*  Chat messages data                                                 */
/* ------------------------------------------------------------------ */

const chatMessagesSv = [
  { sender: "customer" as const, text: "Hej! När kommer ni till upphämtningsadressen?" },
  { sender: "ungdomskraft" as const, text: "Hej Emma! Hanna & Lukas är på väg och beräknas vara framme om ca 5 minuter \u{1F69B}" },
  { sender: "customer" as const, text: "Perfekt, tack för det snabba svaret!" },
  { sender: "ungdomskraft" as const, text: "Varsågod! Hör av dig om du behöver mer hjälp \u{1F60A}" },
];

const chatMessagesEn = [
  { sender: "customer" as const, text: "Hi! When will you arrive at the pickup address?" },
  { sender: "ungdomskraft" as const, text: "Hi Emma! Hanna & Lukas are on their way and should be there in about 5 minutes \u{1F69B}" },
  { sender: "customer" as const, text: "Perfect, thanks for the quick reply!" },
  { sender: "ungdomskraft" as const, text: "You're welcome! Let us know if you need anything else \u{1F60A}" },
];

const supportT = {
  sv: {
    chatLabel: "Kundsupport från riktiga människor",
    chatTitle: "Dedikerad support",
    chatDesc: "Vår kundtjänst följer med dig från bokning till jobbet är klart. Via vår chatt eller samtal till växeln får du snabba svar direkt, utan väntetid.",
    moversLabel: "Tryggt och pålitligt",
    moversTitle: "Pålitliga ungdomar",
    moversDesc: "Noggrant utvalda och högt betygsatta – våra ungdomar dyker upp i tid, hanterar dina saker med omsorg och ser till att jobbet görs ordentligt.",
    ratingLabel: "Ungdomskraft betyg",
    qual1Title: "Anställd med schyssta villkor",
    qual1Desc: "Trygg anställning med kollektivavtalsenliga villkor och trivsam arbetsplats.",
    qual2Title: "Serviceminded och effektiv",
    qual2Desc: "Lösningsorienterat arbetssätt, effektiv och med en servicenivå som gör skillnad.",
  },
  en: {
    chatLabel: "Customer support from real people",
    chatTitle: "Dedicated support",
    chatDesc: "Our support team follows you from booking to job completion. Via our chat or phone line you get quick answers - no waiting.",
    moversLabel: "Safe and reliable",
    moversTitle: "Reliable young workers",
    moversDesc: "Carefully selected and highly rated - our young workers show up on time, handle your belongings with care, and make sure the job is done right.",
    ratingLabel: "Ungdomskraft rating",
    qual1Title: "Employed with fair conditions",
    qual1Desc: "Secure employment with collective agreement terms and a great workplace.",
    qual2Title: "Service-minded and efficient",
    qual2Desc: "Solution-oriented approach, efficient, and with a level of service that makes a difference.",
  },
};

/* ------------------------------------------------------------------ */
/*  Star rating row                                                    */
/* ------------------------------------------------------------------ */

function Stars({ count = 5 }: { count?: number }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(count)].map((_, i) => (
        <svg
          key={i}
          className="w-3 h-3 text-brand-secondary"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Chat column (left side)                                            */
/* ------------------------------------------------------------------ */

function ChatColumn({ locale }: { locale: Locale }) {
  const l = supportT[locale];
  const chatMessages = locale === "en" ? chatMessagesEn : chatMessagesSv;

  return (
    <div className="flex flex-col h-full p-8 sm:p-10 lg:py-12 lg:pl-36 lg:pr-36">
      {/* Label */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
          <svg className="w-3 h-3 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <span className="text-[13px] font-medium text-white/60">
          {l.chatLabel}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-2xl sm:text-[28px] font-bold text-white mb-3 leading-tight">
        {l.chatTitle}
      </h3>

      {/* Description */}
      <p className="text-[15px] text-white/70 leading-relaxed mb-10 max-w-[380px]">
        {l.chatDesc}
      </p>

      {/* Chat mockup — staggered whileInView (same pattern as FeatureCards) */}
      <div className="flex flex-col gap-3 mt-auto">
        {chatMessages.map((msg, i) => {
          const isCustomer = msg.sender === "customer";
          return (
            <motion.div
              key={i}
              className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: `${-100 - i * 50}px 0px` }}
              transition={{ duration: 0.4 }}
            >
              {!isCustomer && (
                <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 mr-2 mt-auto mb-1 shadow-sm">
                  <Image
                    src="/uk-icon.png"
                    alt="Ungdomskraft"
                    width={24}
                    height={24}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div
                className={`max-w-[80%] px-4 py-2.5 text-[13px] leading-relaxed ${
                  isCustomer
                    ? "bg-white/15 text-white rounded-2xl rounded-br-sm"
                    : "bg-white text-gray-800 rounded-2xl rounded-bl-sm shadow-sm"
                }`}
              >
                {msg.text}
              </div>
              {isCustomer && (
                <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 ml-2 mt-auto mb-1 shadow-sm">
                  <Image
                    src="/avatar-5.png"
                    alt="Kund"
                    width={24}
                    height={24}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Movers column (right side — white card floating on gradient)       */
/* ------------------------------------------------------------------ */

function MoversColumn({ locale }: { locale: Locale }) {
  const l = supportT[locale];

  return (
    <div className="flex flex-col h-full pt-8 sm:pt-10 lg:pt-12 px-8 sm:px-10 lg:pl-36 lg:pr-12">
      {/* Label */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
          <svg className="w-3 h-3 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <span className="text-[13px] font-medium text-white/60">
          {l.moversLabel}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-2xl sm:text-[28px] font-bold text-white mb-3 leading-tight">
        {l.moversTitle}
      </h3>

      {/* Description */}
      <p className="text-[15px] text-white/70 leading-relaxed mb-8 max-w-[380px]">
        {l.moversDesc}
      </p>

      {/* Stacked profile cards */}
      <div className="relative mt-auto max-w-[380px] w-full">
        {/* Lukas — back card */}
        <div
          className="bg-white/95 rounded-2xl shadow-lg px-5 py-3.5 flex items-center gap-3 ml-2 mr-2"
        >
          <Image
            src="/team-eugene.png"
            alt="Lukas"
            width={60}
            height={60}
            className="w-[60px] h-[60px] rounded-full object-cover shrink-0 ring-2 ring-white"
          />
          <div className="min-w-0">
            <p className="font-bold text-brand-dark text-xl leading-tight">Lukas</p>
            <p className="text-[12px] text-gray-400">Runner</p>
          </div>
          <div className="ml-auto flex flex-col items-end shrink-0">
            <p className="text-[10px] text-gray-400">{l.ratingLabel}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <Stars />
              <span className="text-[15px] font-bold text-brand-dark">4.8</span>
            </div>
          </div>
        </div>

        {/* Hanna — front card (slides up into view) */}
        <motion.div
          className="relative z-10 -mt-14 rounded-t-2xl shadow-xl"
          initial={{ y: 40 }}
          whileInView={{ y: 5 }}
          viewport={{ once: false, margin: "-320px 0px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* White background that extends way past the bottom */}
          <div className="absolute inset-0 bg-white rounded-t-2xl" style={{ bottom: "-200px" }} />
          {/* Profile row */}
          <div className="relative z-10 px-6 pt-5 pb-4 flex items-center gap-4 bg-gray-100 rounded-t-2xl">
            <Image
              src="/team-viktor.png"
              alt="Hanna"
              width={60}
              height={60}
              className="w-[60px] h-[60px] rounded-full object-cover shrink-0 ring-2 ring-white"
            />
            <div className="min-w-0">
              <p className="font-bold text-brand-dark text-xl leading-tight">Hanna</p>
              <p className="text-[12px] text-gray-400">Runner</p>
            </div>
            <div className="ml-auto flex flex-col items-end shrink-0">
              <p className="text-[10px] text-gray-400">{l.ratingLabel}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <Stars />
                <span className="text-[15px] font-bold text-brand-dark">4.9</span>
              </div>
            </div>
          </div>

          {/* Qualifications */}
          <div className="relative z-10 px-6 pb-10 pt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[14px] font-semibold text-brand-dark">{l.qual1Title}</p>
                  <p className="text-[12px] text-gray-400 mt-0.5">{l.qual1Desc}</p>
                </div>
                <div className="w-8 h-8 rounded-full border-2 border-brand-secondary/40 flex items-center justify-center shrink-0 ml-4">
                  <svg className="w-4 h-4 text-brand-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <div className="border-t border-gray-100" />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[14px] font-semibold text-brand-dark">{l.qual2Title}</p>
                  <p className="text-[12px] text-gray-400 mt-0.5">{l.qual2Desc}</p>
                </div>
                <div className="w-8 h-8 rounded-full border-2 border-brand-secondary/40 flex items-center justify-center shrink-0 ml-4">
                  <svg className="w-4 h-4 text-brand-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main exported component                                            */
/* ------------------------------------------------------------------ */

export default function SupportAndMovers({ locale = "sv" }: { locale?: Locale }) {
  return (
    <section className="pt-8 pb-0 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl sm:px-6 xl:px-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="relative rounded-none sm:rounded-3xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 min-h-[620px]"
          style={{
            background: "radial-gradient(ellipse 60% 50% at 30% 20%, rgba(248,149,68,0.08) 0%, transparent 70%), radial-gradient(ellipse 50% 50% at 80% 80%, rgba(255,255,255,0.04) 0%, transparent 70%), linear-gradient(135deg, #2a4a63 0%, #1d3547 50%, #2a4a63 100%)",
          }}
        >

          {/* Left — Dedicated support */}
          <div className="relative z-10">
            <ChatColumn locale={locale} />
          </div>

          {/* Right — Trusted movers */}
          <div className="relative z-10">
            <MoversColumn locale={locale} />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
